import { useEffect, useMemo, useRef, useState } from "react";
import { CheckSquare, Loader2, Paperclip, Plus, Send, Vote, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, formatErr, uploadFile } from "@/lib/api";
import { UserAvatar } from "@/components/common";

export default function Composer({ me, chatUsers, replyTo, editing, meetingTag, onCancel, onPoll, onChecklist }) {
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState({});
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mq, setMq] = useState(null);
  const ta = useRef(null);

  useEffect(() => {
    if (editing) setText(editing.text);
    ta.current?.focus();
  }, [editing, replyTo]);

  const candidates = useMemo(
    () => (mq === null ? [] : chatUsers.filter((u) => u.id !== me.id && u.name.toLowerCase().includes(mq.toLowerCase())).slice(0, 6)),
    [mq, chatUsers, me.id],
  );

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    const before = v.slice(0, e.target.selectionStart);
    const m = before.match(/(?:^|\s)@([\w. ]{0,20})$/);
    setMq(m ? m[1] : null);
  };

  const pickMention = (u) => {
    const pos = ta.current.selectionStart;
    const before = text.slice(0, pos).replace(/@([\w. ]{0,20})$/, `@${u.name} `);
    setText(before + text.slice(pos));
    setMentions((p) => ({ ...p, [u.id]: u.name }));
    setMq(null);
    ta.current.focus();
  };

  const attach = async (list) => {
    setBusy(true);
    for (const f of Array.from(list || [])) {
      try {
        const r = await uploadFile(f, "chat_attachment");
        setFiles((p) => [...p, { path: r.path, name: r.name, content_type: r.content_type }]);
      } catch (e) {
        toast.error(`${f.name}: ${formatErr(e)}`);
      }
    }
    setBusy(false);
  };

  const reset = () => {
    setText("");
    setMentions({});
    setFiles([]);
    setMq(null);
    onCancel();
  };

  const send = async () => {
    if (busy || (!text.trim() && !files.length)) return;
    setBusy(true);
    try {
      if (editing) await api.patch(`/chat/messages/${editing.id}`, { text });
      else {
        const ids = Object.entries(mentions).filter(([, n]) => text.includes(`@${n}`)).map(([id]) => id);
        await api.post("/chat/messages", { text, attachments: files, mentions: ids, reply_to: replyTo?.id || null, meeting_tag: meetingTag });
      }
      reset();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const banner = editing ? { label: "Editing message", text: editing.text } : replyTo ? { label: `Replying to ${replyTo.sender_name}`, text: replyTo.text || replyTo.poll?.question || replyTo.checklist?.title || "Attachment" } : null;
  return (
    <div className="relative border-t bg-card p-2 sm:p-3" data-testid="chat-composer">
      {candidates.length > 0 && (
        <div className="absolute bottom-full left-3 mb-1 w-64 overflow-hidden rounded-xl border bg-popover shadow-lg" data-testid="mention-suggestions">
          {candidates.map((u) => (
            <button key={u.id} onMouseDown={(e) => { e.preventDefault(); pickMention(u); }} data-testid={`mention-option-${u.id}`} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-secondary">
              <UserAvatar user={u} />{u.name}
            </button>
          ))}
        </div>
      )}
      {banner && (
        <div className="mb-2 flex items-start justify-between gap-2 rounded-lg border-l-4 border-primary bg-secondary/60 px-3 py-1.5 text-xs" data-testid="composer-banner">
          <span className="min-w-0"><span className="font-semibold">{banner.label}</span><span className="block truncate text-muted-foreground">{banner.text}</span></span>
          <button onClick={reset} data-testid="composer-cancel"><X className="h-4 w-4" /></button>
        </div>
      )}
      {files.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {files.map((f, i) => (
            <span key={f.path} className="flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs"><FileText className="h-3.5 w-3.5" />{f.name}
              <button onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button></span>
          ))}
        </div>
      )}
      <div className="flex items-end gap-1.5">
        {!editing && (
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="composer-plus" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary"><Plus className="h-5 w-5" /></DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={onPoll} data-testid="composer-poll"><Vote className="mr-2 h-4 w-4" />Poll</DropdownMenuItem>
              <DropdownMenuItem onClick={onChecklist} data-testid="composer-checklist"><CheckSquare className="mr-2 h-4 w-4" />Checklist</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {!editing && (
          <label className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground hover:bg-secondary" data-testid="composer-attach">
            <Paperclip className="h-5 w-5" />
            <input type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid="composer-file-input" onChange={(e) => { attach(e.target.files); e.target.value = ""; }} />
          </label>
        )}
        <textarea
          ref={ta}
          data-testid="composer-input"
          rows={1}
          value={text}
          onChange={onChange}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.innerWidth >= 768) { e.preventDefault(); send(); } }}
          placeholder="Message (@ to mention)"
          className="max-h-32 min-h-10 flex-1 resize-none rounded-2xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:border-primary"
        />
        <button onClick={send} disabled={busy} data-testid="composer-send" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform active:scale-95 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
